import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Phone, Mail, MapPin } from "lucide-react";
import { Navigation } from "./Navigation";

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Services & Pricing",
      questions: [
        {
          q: "How much does a full respray cost?",
          a: "Full respray costs vary depending on vehicle size, paint type, and prep work required. Generally, prices range from $3,500 for a small hatch to $8,000+ for larger vehicles or custom finishes. Contact us for an accurate quote for your specific vehicle."
        },
        {
          q: "Do you do insurance work?",
          a: "No, we don't take on insurance jobs. Insurance work often dictates which materials, paint systems, and procedures we can use - and that's not how we operate. We prefer to have complete control over the process from start to finish, using the products and techniques we trust. That way, we can stand behind every job with our name and back it with our warranty. If you're paying out of pocket or want work done to a higher standard than what insurance would cover, we're your guys."
        },
        {
          q: "What's the difference between a budget respray and a show-quality finish?",
          a: "A budget respray covers the basics - sand, prime, paint, clear. A show-quality finish includes more extensive prep work, multiple coats, wet sanding, machine polishing, and ceramic coating. The difference is noticeable in the depth, gloss, and longevity of the finish."
        },
        {
          q: "Do you offer ceramic coating?",
          a: "Yes! We offer professional ceramic coating application. This provides long-lasting protection and makes maintenance much easier. It's highly recommended after a fresh paint job."
        },
        {
          q: "Can you match my original factory colour?",
          a: "Absolutely. We use computer colour-matching technology to perfectly match your factory paint code. We can also colour match from physical samples if needed."
        }
      ]
    },
    {
      category: "Process & Timeline",
      questions: [
        {
          q: "How long does a full respray take?",
          a: "A typical full respray takes 2-4 weeks depending on the scope of work. Complex custom jobs or extensive bodywork can take longer. We'll give you a realistic timeline when you book in."
        },
        {
          q: "Can I track my car's progress?",
          a: "Yes! When you drop off your car, we'll send you a unique tracking code. You can view real-time progress photos and status updates on our website anytime."
        },
        {
          q: "Do I need to book in advance?",
          a: "Yes, we work by appointment only. We recommend booking 2-4 weeks ahead, especially during busy periods. Call us to check current availability."
        },
        {
          q: "What's your process for resprays?",
          a: "Our process includes: 1) Initial inspection & quote, 2) Disassembly & masking, 3) Bodywork & repairs, 4) Primer & blocking, 5) Paint application, 6) Clear coat, 7) Wet sand & polish, 8) Reassembly & final detail. We document every stage with photos."
        }
      ]
    },
    {
      category: "Paint Care & Warranty",
      questions: [
        {
          q: "How do I care for my new paint job?",
          a: "Wait 90 days before waxing or applying any coatings. Hand wash only with pH-neutral soap. Avoid automatic car washes. Keep it out of direct sunlight when possible. Clean off bird droppings, bugs, and tree sap immediately. We'll send you a detailed aftercare guide when your car is ready."
        },
        {
          q: "Do you offer a warranty?",
          a: "Yes, we stand behind our work. Paint jobs come with a warranty against peeling, flaking, and defects in our workmanship. Warranty length depends on the service level. Ask us for specific details."
        },
        {
          q: "When can I wax or ceramic coat my new paint?",
          a: "Wait at least 90 days. Fresh paint needs time to fully cure and outgas. Applying wax or coatings too early can trap solvents and cause issues down the track."
        }
      ]
    },
    {
      category: "Booking & Payment",
      questions: [
        {
          q: "How do I get a quote?",
          a: "You can request a quote through our website - just upload some photos of your car and tell us what you're after. Alternatively, bring your car in for an in-person assessment. We'll get back to you within 24-48 hours."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept bank transfer, cash, and card payments. A deposit is required to secure your booking, with the balance due on completion."
        },
        {
          q: "Where are you located?",
          a: "We're at 101 Norman Street, Woolloongabba, Brisbane. Easy access from the CBD and surrounding suburbs. There's street parking available."
        },
        {
          q: "What are your operating hours?",
          a: "We're open 7 days a week, from 11am onwards. Appointments only - no walk-ins. Give us a call to book a time."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="pt-24 pb-20 px-6 max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl uppercase tracking-wider mb-4">
            FAQ
          </h1>
          <p className="text-zinc-400 font-body text-lg">
            Common questions about our services
          </p>
        </motion.div>

        {/* FAQ Sections */}
        {faqs.map((section, sectionIndex) => (
          <motion.div
            key={section.category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: sectionIndex * 0.1, duration: 0.4 }}
            className="mb-12"
          >
            <h2 className="font-heading text-xl uppercase tracking-wider text-[#3b82f6] mb-6">
              {section.category}
            </h2>
            
            <div className="space-y-2">
              {section.questions.map((faq, i) => {
                const globalIndex = `${sectionIndex}-${i}`;
                const isOpen = openIndex === globalIndex;
                
                return (
                  <div
                    key={i}
                    className="border border-white/10 bg-white/[0.02]"
                  >
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                      className="w-full p-5 flex items-center justify-between text-left bg-transparent border-none cursor-pointer"
                    >
                      <span className="font-body text-white pr-4">{faq.q}</span>
                      <ChevronDown
                        size={18}
                        className={`text-zinc-500 transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-5 pt-0">
                            <p className="text-zinc-400 font-body leading-relaxed">
                              {faq.a}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ))}

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-16 p-8 bg-white/5 border border-white/10"
        >
          <h3 className="font-heading text-2xl uppercase tracking-wide mb-4 text-center">
            Still Have Questions?
          </h3>
          <p className="text-zinc-400 font-body text-center mb-8">
            Get in touch - we&apos;re happy to help
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <a
              href="tel:0450645926"
              className="flex flex-col items-center gap-2 text-white no-underline hover:text-[#3b82f6] transition-colors"
            >
              <Phone size={24} className="text-[#3b82f6]" />
              <span className="font-body">0450 645 926</span>
            </a>
            <a
              href="mailto:sales@velocitypaintandpanel.com"
              className="flex flex-col items-center gap-2 text-white no-underline hover:text-[#3b82f6] transition-colors"
            >
              <Mail size={24} className="text-[#3b82f6]" />
              <span className="font-body text-sm">sales@velocitypaintandpanel.com</span>
            </a>
            <div className="flex flex-col items-center gap-2">
              <MapPin size={24} className="text-[#3b82f6]" />
              <span className="font-body text-sm">101 Norman St, Woolloongabba</span>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/book"
              className="inline-block px-10 py-4 bg-white text-black font-heading text-sm uppercase tracking-wider hover:bg-zinc-200 transition-colors no-underline"
            >
              Get a Quote
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQPage;
